// Who else is at a table, from the venue's own tape: every bet opened in the tape's window and not
// yet settled is a player in the hand, their stake the chips in front of them. A bet that settles
// shows its result at the seat for a moment. Dollars at eight places, never floats.

import { useEffect, useMemo, useRef, useState } from "react";
import type { Trade } from "../sdk";
import { useRecentTrades } from "../state/market";
import { toRaw } from "../lib/num";

export type Result = { seq: string; stake: bigint; payout: bigint; bust: boolean; seen: number };

export type Player = {
  wallet: string;
  /** Riding now, dollars at eight places. */
  stake: bigint;
  isLong: boolean;
  /** The latest bet that settled, while it is fresh. */
  result: Result | null;
};

const SHOWN_MS = 6_000;
const dollars = (text: string | null) => toRaw(text ?? "0", 8) ?? 0n;
const stakeOf = (trade: Trade) => (trade.leverage > 0 ? dollars(trade.notionalUsd) / BigInt(trade.leverage) : 0n);

/** The players at `instrumentId`'s table and the pot they make, the follower's own wallet left out. */
export function useRoom(instrumentId: number | undefined, owner: string | null) {
  const trades = useRecentTrades().data;
  // settlements as this page first saw them: the tape's first read is history, not news
  const firstSeen = useRef<Map<string, number> | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  return useMemo(() => {
    const me = owner?.toLowerCase();
    const players = new Map<string, Player>();
    if (!trades || instrumentId === undefined) return { players: [] as Player[], pot: 0n };
    const known = firstSeen.current;
    const seen = known ?? new Map<string, number>();
    const closed = new Set<string>();
    for (const trade of trades) if (trade.kind !== "open") closed.add(trade.positionId);

    // oldest first, so the latest side wins
    for (const trade of [...trades].reverse()) {
      if (trade.instrumentId !== instrumentId || trade.wallet.toLowerCase() === me) continue;
      const wallet = trade.wallet.toLowerCase();
      const player = players.get(wallet) ?? { wallet, stake: 0n, isLong: trade.isLong, result: null };
      if (trade.kind === "open") {
        if (!closed.has(trade.positionId)) {
          player.stake += stakeOf(trade);
          player.isLong = trade.isLong;
        }
      } else {
        if (!seen.has(trade.seq)) seen.set(trade.seq, known ? Date.now() : 0);
        const at = seen.get(trade.seq) ?? 0;
        if (now - at < SHOWN_MS) {
          const stake = stakeOf(trade);
          const bust = trade.kind === "liquidation";
          const after = stake + dollars(trade.pnlUsd);
          player.result = { seq: trade.seq, stake, payout: bust || after < 0n ? 0n : after, bust, seen: at };
          player.isLong = trade.isLong;
        }
      }
      players.set(wallet, player);
    }
    firstSeen.current = seen;
    const list = [...players.values()].filter((player) => player.stake > 0n || player.result);
    list.sort((a, b) => (b.stake > a.stake ? 1 : b.stake < a.stake ? -1 : 0));
    const pot = list.reduce((sum, player) => sum + player.stake, 0n);
    return { players: list, pot };
  }, [trades, instrumentId, owner, now]);
}

/** Keeps each player in the same seat while they stay: newcomers take the free seats in turn. */
export function useSeating(players: Player[], count: number): (Player | null)[] {
  const held = useRef<(string | null)[]>([]);
  const byWallet = new Map(players.map((player) => [player.wallet, player]));
  const seats = Array.from({ length: count }, (_, index) => held.current[index] ?? null).map((wallet) => (wallet && byWallet.has(wallet) ? wallet : null));
  const seated = new Set(seats.filter(Boolean));
  for (const player of players) {
    if (seated.has(player.wallet)) continue;
    const free = seats.indexOf(null);
    if (free < 0) break;
    seats[free] = player.wallet;
    seated.add(player.wallet);
  }
  held.current = seats;
  return seats.map((wallet) => (wallet ? (byWallet.get(wallet) ?? null) : null));
}
